// src/pages/Requisicoes.js - Gestão de Requisições
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, TextField, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Alert, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, MenuItem, FormControl,
  InputLabel, Select, TablePagination, Tooltip, Collapse, Divider,
  FormControlLabel, Switch
} from '@mui/material';
import {
  Assignment as AssignmentIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, Refresh as RefreshIcon, FilterList as FilterIcon,
  Clear as ClearIcon, CalendarToday as CalendarIcon, Person as PersonIcon,
  Business as BusinessIcon, KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon, CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const formatarData = (valor) => {
  if (!valor) return null;
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
};

const num = (v) => (v === null || v === undefined ? null : Number(v).toLocaleString('pt-BR'));

// Junta rotulo e nome so quando os dois existem, para nao imprimir "null — null".
const par = (codigo, nome) => {
  if (codigo === null || codigo === undefined || codigo === '') return nome || null;
  return nome ? `${codigo} — ${nome}` : String(codigo);
};

// TPCMP da FC12110. O 'R' e a explosao teorica da base (aerosil, lactose,
// celulose) e sozinho responde por 51% das linhas: numa ficha comum os itens
// caem de 24 para 10 quando ele sai. Por isso vem escondido por padrao.
const TIPO_COMPONENTE = {
  C: 'Ativo prescrito',
  X: 'Excipiente',
  E: 'Embalagem',
  P: 'Cápsula / invólucro',
  R: 'Explosão da base',
  H: 'Homeopático',
  F: 'Fórmula',
  S: 'Solução'
};

// Fora do componente de proposito: definido dentro do render, cada setState
// criava um TIPO novo de componente e o React remontava a grade inteira.
const Campo = ({ rotulo, valor, destaque }) => (
  <Grid item xs={6} sm={4} md={3}>
    <Typography variant="caption" color="text.secondary" display="block">{rotulo}</Typography>
    <Typography variant="body2" fontWeight={destaque ? 'bold' : 'normal'}
      color={destaque ? 'primary.main' : 'text.primary'}>
      {valor === null || valor === undefined || valor === '' ? '-' : valor}
    </Typography>
  </Grid>
);

// Mesma regra do servidor: codigo_requisicao = CDFIL(3) + NRRQU(6) + SERIER(2).
// Cerca de 0,8% das linhas nao seguem esse layout — sao bipes de ordem de
// producao gravados na mesma tabela, que resolvem na FC18000 e nao na FC12100.
// Checar aqui evita uma ida ao Firebird que so pode dar 422 e, de quebra, evita
// que o apiCall marque a API inteira como fora do ar por um caso esperado.
const temLayoutDeFicha = (codigo) => {
  const cod = String(codigo || '').trim();
  if (!/^\d{11}$/.test(cod)) return false;
  return cod.slice(9, 11).replace(/^0(?=.)/, '').length === 1;
};

const SEM_FICHA = 'Este código não segue o formato de ficha (filial + requisição + série), '
  + 'então não há o que buscar no FormulaCerta.';

// INDCONFERIDO tem tres estados, nao dois. 'P' e o inicial e responde por 92,8%
// das fichas — mostrar tudo isso como "Não" faria a tela parecer que quase nada
// foi conferido.
const CONFERIDO = { S: 'Sim', N: 'Não', P: 'Pendente' };

const TituloBloco = ({ children }) => (
  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary"
    sx={{ mt: 2, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
    {children}
  </Typography>
);

const Requisicoes = () => {
  const [requisicoes, setRequisicoes] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [setores, setSetores] = useState([]);
  const [tiposQuantidade, setTiposQuantidade] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filtros
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    funcionario: '',
    setor: '',
    tipoQuantidade: '',
    codigoRequisicao: ''
  });

  // Paginação
  const [paginacao, setPaginacao] = useState({
    pagina: 1,
    limite: 25,
    total: 0,
    totalPaginas: 0
  });

  // Dialog de edição
  const [dialogAberto, setDialogAberto] = useState(false);
  const [requisicaoEditando, setRequisicaoEditando] = useState(null);
  const [dadosFormulario, setDadosFormulario] = useState({
    codigo_requisicao: '',
    data: '',
    codigo_funcionario: '',
    setor: '',
    quantidade: '',
    tipo_quantidade: ''
  });
  const [salvando, setSalvando] = useState(false);

  // Painel expandido: uma entrada por linha aberta, chaveada pelo id do bipe.
  const [expandida, setExpandida] = useState(null);
  const [detalhe, setDetalhe] = useState({});
  // Espelha `expandida` para o handler assincrono conseguir descobrir, depois
  // do await, que o usuario ja fechou a linha ou trocou de pagina.
  const expandidaRef = useRef(null);

  const { api, user } = useAuth();
  const podeEditar = user?.nivel >= 3;

  const carregarDados = useCallback(async () => {
    setLoading(true);
    setError('');
    // A lista vai mudar debaixo do painel: fecha o que estiver aberto e joga
    // fora o cache, senao o detalhe de um id sobrevive a um filtro novo.
    setExpandida(null);
    expandidaRef.current = null;
    setDetalhe({});
    try {
      const params = {
        pagina: paginacao.pagina,
        limite: paginacao.limite
      };

      // Adicionar filtros ativos
      if (filtros.dataInicio) params.dataInicio = filtros.dataInicio;
      if (filtros.dataFim) params.dataFim = filtros.dataFim;
      if (filtros.funcionario) params.funcionario = filtros.funcionario;
      if (filtros.setor) params.setor = filtros.setor;
      if (filtros.codigoRequisicao) params.codigoRequisicao = filtros.codigoRequisicao;
      if (filtros.tipoQuantidade) params.tipoQuantidade = filtros.tipoQuantidade;

      const response = await api.listarRequisicoes(params);

      if (response.success) {
        setRequisicoes(response.dados || []);
        // respostaSucesso() espalha os extras no nivel raiz da resposta, nao
        // dentro de `meta` — com o caminho errado o total ficava sempre 0 e o
        // cabecalho mostrava "0 registros" com a tabela cheia.
        if (response.paginacao) {
          setPaginacao(prev => ({
            ...prev,
            total: response.paginacao.total,
            totalPaginas: response.paginacao.totalPaginas
          }));
        }
      } else {
        setError(response.message || 'Erro ao carregar requisições');
      }
    } catch (err) {
      setError(err.message || 'Erro de conexão ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [api, filtros, paginacao.pagina, paginacao.limite]);

  const carregarFiltros = async () => {
    try {
      const [resFuncionarios, resSetores, resTipos] = await Promise.all([
        api.listarFuncionarios(),
        api.listarSetores(),
        api.listarTiposQuantidade()
      ]);

      if (resFuncionarios.success) {
        setFuncionarios(resFuncionarios.dados || []);
      }
      if (resSetores.success) {
        setSetores(resSetores.dados || []);
      }
      if (resTipos.success) {
        setTiposQuantidade(resTipos.dados || []);
      }
    } catch (err) {
      console.error('Erro ao carregar filtros:', err);
    }
  };

  const aplicarFiltros = () => {
    setPaginacao(prev => ({ ...prev, pagina: 1 }));
    carregarDados();
  };

  const limparFiltros = () => {
    setFiltros({
      dataInicio: '',
      dataFim: '',
      funcionario: '',
      setor: '',
      tipoQuantidade: '',
      codigoRequisicao: ''
    });
    setPaginacao(prev => ({ ...prev, pagina: 1 }));
  };

  const abrirDialogEditar = (requisicao) => {
    setRequisicaoEditando(requisicao);
    // Formatar data para input datetime-local
    let dataFormatada = '';
    if (requisicao.data) {
      const d = new Date(requisicao.data);
      dataFormatada = d.toISOString().slice(0, 16);
    }
    setDadosFormulario({
      codigo_requisicao: requisicao.codigo_requisicao || '',
      data: dataFormatada,
      codigo_funcionario: requisicao.codigo_funcionario || '',
      setor: requisicao.setor || '',
      quantidade: requisicao.quantidade || '',
      tipo_quantidade: requisicao.tipo_quantidade || ''
    });
    setDialogAberto(true);
  };

  const salvarRequisicao = async () => {
    setError('');
    setSuccess('');
    setSalvando(true);

    try {
      // Preparar dados para envio
      const dadosEnvio = {};
      if (dadosFormulario.codigo_requisicao) {
        dadosEnvio.codigo_requisicao = dadosFormulario.codigo_requisicao;
      }
      if (dadosFormulario.data) {
        dadosEnvio.data = dadosFormulario.data.replace('T', ' ') + ':00';
      }
      if (dadosFormulario.codigo_funcionario) {
        dadosEnvio.codigo_funcionario = dadosFormulario.codigo_funcionario;
      }
      dadosEnvio.setor = dadosFormulario.setor;
      dadosEnvio.quantidade = dadosFormulario.quantidade;
      dadosEnvio.tipo_quantidade = dadosFormulario.tipo_quantidade;

      const response = await api.atualizarRequisicao(requisicaoEditando.id, dadosEnvio);

      if (response.success) {
        setSuccess(response.message || 'Requisição atualizada com sucesso!');
        esquecerDetalhe(requisicaoEditando.id);
        setTimeout(() => {
          setDialogAberto(false);
          setSuccess('');
          carregarDados();
        }, 1500);
      } else {
        throw new Error(response.message || 'Erro ao atualizar requisição');
      }
    } catch (err) {
      setError(err.message || 'Erro ao salvar requisição');
    } finally {
      setSalvando(false);
    }
  };

  // As duas pernas do painel carregam e falham de forma INDEPENDENTE, e cada uma
  // guarda o proprio "ja carregou". Uma flag unica para as duas dava dois
  // defeitos: o Firebird voltando primeiro marcava a linha inteira como pronta e
  // o erro do SQL Server ficava preso sem botao de tentar de novo; e o retry da
  // ficha apagava trilha e auditoria que ja estavam na tela.
  const carregarDadosBipe = useCallback(async (req) => {
    setDetalhe(d => ({ ...d, [req.id]: { ...d[req.id], carregandoDados: true } }));
    try {
      const r = await api.detalhesRequisicao(req.id);
      // O usuario pode ter fechado a linha ou trocado de pagina durante a espera.
      if (expandidaRef.current !== req.id) return;
      setDetalhe(d => ({
        ...d,
        [req.id]: {
          ...d[req.id],
          carregandoDados: false,
          carregadoDados: true,
          requisicao: r?.dados?.requisicao || null,
          trilha: r?.dados?.trilha || [],
          auditoria: r?.dados?.auditoria || [],
          totais: r?.dados?.totais || null,
          erro: null
        }
      }));
    } catch (e) {
      if (expandidaRef.current !== req.id) return;
      setDetalhe(d => ({
        ...d,
        [req.id]: {
          ...d[req.id],
          carregandoDados: false,
          carregadoDados: false,
          erro: e.message || 'Não foi possível carregar os dados deste bipe'
        }
      }));
    }
  }, [api]);

  const carregarFicha = useCallback(async (req) => {
    // Codigo fora do layout nunca vai achar ficha — marca como resolvido para
    // nao ficar oferecendo um "tentar de novo" que jamais daria certo.
    if (!temLayoutDeFicha(req.codigo_requisicao)) {
      setDetalhe(d => ({
        ...d,
        [req.id]: { ...d[req.id], carregandoFicha: false, carregadoFicha: true, ficha: null, erroFicha: SEM_FICHA }
      }));
      return;
    }
    setDetalhe(d => ({ ...d, [req.id]: { ...d[req.id], carregandoFicha: true } }));
    try {
      const r = await api.fichaRequisicaoFirebird(req.id);
      if (expandidaRef.current !== req.id) return;
      setDetalhe(d => ({
        ...d,
        [req.id]: { ...d[req.id], carregandoFicha: false, carregadoFicha: true, ficha: r?.dados || null, erroFicha: null }
      }));
    } catch (e) {
      if (expandidaRef.current !== req.id) return;
      setDetalhe(d => ({
        ...d,
        [req.id]: {
          ...d[req.id],
          carregandoFicha: false,
          carregadoFicha: false,
          ficha: null,
          erroFicha: e.message || 'Ficha indisponível no Firebird'
        }
      }));
    }
  }, [api]);

  // Busca o detalhe uma vez por linha e guarda: reabrir nao consulta de novo.
  const alternarExpansao = (req) => {
    if (expandida === req.id) {
      setExpandida(null);
      expandidaRef.current = null;
      return;
    }
    setExpandida(req.id);
    expandidaRef.current = req.id;
    const d = detalhe[req.id];
    if (!d?.carregadoDados) carregarDadosBipe(req);
    if (!d?.carregadoFicha) carregarFicha(req);
  };

  // Reabrir tem de mostrar o valor novo, nao o que foi buscado antes da edicao.
  const esquecerDetalhe = (id) => setDetalhe(d => {
    if (!(id in d)) return d;
    const copia = { ...d };
    delete copia[id];
    return copia;
  });

  const alternarBase = (id) => setDetalhe(d => ({
    ...d, [id]: { ...d[id], mostrarBase: !d[id]?.mostrarBase }
  }));

  const excluirRequisicao = async (requisicao) => {
    if (!window.confirm(`Tem certeza que deseja excluir a requisição ${requisicao.codigo_requisicao}?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await api.excluirRequisicao(requisicao.id);

      if (response.success) {
        setSuccess(response.message || 'Requisição excluída com sucesso!');
        esquecerDetalhe(requisicao.id);
        await carregarDados();
      } else {
        throw new Error(response.message || 'Erro ao excluir requisição');
      }
    } catch (err) {
      setError(err.message || 'Erro ao excluir requisição');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPaginacao(prev => ({ ...prev, pagina: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPaginacao(prev => ({
      ...prev,
      limite: parseInt(event.target.value, 10),
      pagina: 1
    }));
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarDados();
  }, [paginacao.pagina, paginacao.limite]);

  // Efeito para recarregar quando filtros são limpos
  useEffect(() => {
    if (!filtros.dataInicio && !filtros.dataFim && !filtros.funcionario && !filtros.setor && !filtros.tipoQuantidade && !filtros.codigoRequisicao) {
      carregarDados();
    }
  }, [filtros]);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{
        p: 3, mb: 3,
        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        color: 'white',
        borderRadius: 2
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Gestao de Requisicoes
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Consulta e edicao de requisicoes do sistema
            </Typography>
          </Box>
          <Box>
            <Chip
              label={`${paginacao.total} registros`}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filtros */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <FilterIcon sx={{ mr: 1, color: '#64748b' }} />
            <Typography variant="h6" fontWeight="bold">
              Filtros
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data Inicio"
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <CalendarIcon sx={{ mr: 1, fontSize: 18, color: '#94a3b8' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data Fim"
                type="date"
                value={filtros.dataFim}
                onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <CalendarIcon sx={{ mr: 1, fontSize: 18, color: '#94a3b8' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Funcionario</InputLabel>
                <Select
                  value={filtros.funcionario}
                  onChange={(e) => setFiltros(prev => ({ ...prev, funcionario: e.target.value }))}
                  label="Funcionario"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {funcionarios.map((func) => (
                    <MenuItem key={func.id} value={func.codigo}>
                      {func.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Setor</InputLabel>
                <Select
                  value={filtros.setor}
                  onChange={(e) => setFiltros(prev => ({ ...prev, setor: e.target.value }))}
                  label="Setor"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {setores.map((setor) => (
                    <MenuItem key={setor.id} value={setor.nome}>
                      {setor.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Formula</InputLabel>
                <Select
                  value={filtros.tipoQuantidade}
                  onChange={(e) => setFiltros(prev => ({ ...prev, tipoQuantidade: e.target.value }))}
                  label="Tipo Formula"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {tiposQuantidade.map((tipo) => (
                    <MenuItem key={tipo} value={tipo}>
                      {tipo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Codigo Requisicao"
                value={filtros.codigoRequisicao}
                onChange={(e) => setFiltros(prev => ({ ...prev, codigoRequisicao: e.target.value }))}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, fontSize: 18, color: '#94a3b8' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box display="flex" gap={1} height="100%">
                <Button
                  variant="contained"
                  onClick={aplicarFiltros}
                  fullWidth
                  sx={{ height: 40 }}
                >
                  Filtrar
                </Button>
                <Tooltip title="Limpar filtros">
                  <IconButton onClick={limparFiltros} color="default">
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela de Requisições */}
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              Lista de Requisicoes
            </Typography>
            <IconButton onClick={carregarDados} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : requisicoes.length > 0 ? (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                      <TableCell sx={{ width: 48 }} />
                      <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Codigo Requisicao</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Data/Hora</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Funcionario</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Setor</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">Capsulas</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                      {podeEditar && (
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Acoes</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requisicoes.map((req) => (
                      <React.Fragment key={req.id}>
                      <TableRow hover
                        sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
                        onClick={() => alternarExpansao(req)}>
                        <TableCell>
                          <IconButton size="small"
                            aria-label={expandida === req.id ? 'Recolher' : 'Expandir'}
                            aria-expanded={expandida === req.id}>
                            {expandida === req.id ? <CollapseIcon /> : <ExpandIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {req.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={req.codigo_requisicao}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {req.data_formatada || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ mr: 1, fontSize: 16, color: '#6366f1' }} />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {req.funcionario_nome || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {req.codigo_funcionario}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <BusinessIcon sx={{ mr: 1, fontSize: 16, color: '#10b981' }} />
                            {req.setor_efetivo || 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={req.quantidade || 0}
                            size="small"
                            color={req.quantidade > 0 ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {req.tipo_quantidade || '-'}
                          </Typography>
                        </TableCell>
                        {podeEditar && (
                          <TableCell align="center">
                            {/* stopPropagation: sem ele, clicar em Editar abre o
                                dialogo E expande a linha por baixo. */}
                            <Box display="flex" gap={1} justifyContent="center">
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); abrirDialogEditar(req); }}
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); excluirRequisicao(req); }}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={podeEditar ? 9 : 8} sx={{ py: 0, border: 0 }}>
                          <Collapse in={expandida === req.id} timeout="auto" unmountOnExit>
                            <Box sx={{ my: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>

                              {detalhe[req.id]?.carregandoDados && (
                                <Box display="flex" alignItems="center" gap={2} py={2}>
                                  <CircularProgress size={20} />
                                  <Typography variant="body2">Carregando a requisição...</Typography>
                                </Box>
                              )}

                              {/* O erro mora aqui, por linha. Usar o setError da
                                  pagina faria a mensagem aparecer tambem dentro
                                  do dialogo de edicao, que compartilha o estado. */}
                              {detalhe[req.id]?.erro && (
                                <Alert severity="warning" sx={{ mb: 2 }}
                                  action={
                                    <Button size="small" onClick={(e) => {
                                      e.stopPropagation();
                                      carregarDadosBipe(req);
                                    }}>Tentar de novo</Button>
                                  }>
                                  {detalhe[req.id].erro}
                                </Alert>
                              )}

                              {/* ---------- 1. O que foi gravado neste bipe ---------- */}
                              {detalhe[req.id]?.requisicao && (() => {
                                const r = detalhe[req.id].requisicao;
                                return (
                                  <>
                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                      <Typography variant="h6" fontWeight="bold">
                                        {r.codigo_requisicao}
                                      </Typography>
                                      <Chip size="small" color="primary"
                                        label={r.etapa || r.setor_efetivo} />
                                      {r.forma_nome && <Chip size="small" label={r.forma_nome} />}
                                    </Box>
                                    <TituloBloco>Este bipe</TituloBloco>
                                    <Grid container spacing={2}>
                                      <Campo rotulo="Funcionário"
                                        valor={par(r.codigo_funcionario, r.funcionario_nome)} />
                                      <Campo rotulo="Setor" valor={r.setor_efetivo} />
                                      <Campo rotulo="Data / hora" valor={r.data_formatada} />
                                      <Campo rotulo="Quantidade" destaque
                                        valor={`${num(r.quantidade) ?? '-'} ${r.tipo_quantidade || ''}`.trim()} />
                                      <Campo rotulo="Filial" valor={r.CDFIL} />
                                      <Campo rotulo="Filial destino" valor={r.filial_destino} />
                                      <Campo rotulo="Requisição / série"
                                        valor={r.NRRQU ? `${r.NRRQU} / ${r.SERIER}` : null} />
                                      <Campo rotulo="Terminal (IP)" valor={r.ip_origem} />
                                    </Grid>
                                  </>
                                );
                              })()}

                              {/* ---------- 2 e 3. A ficha no Firebird ---------- */}
                              <TituloBloco>A ficha no FormulaCerta</TituloBloco>
                              {detalhe[req.id]?.carregandoFicha && (
                                <Box display="flex" alignItems="center" gap={2} py={1}>
                                  <CircularProgress size={16} />
                                  <Typography variant="body2">Consultando o FormulaCerta...</Typography>
                                </Box>
                              )}
                              {detalhe[req.id]?.erroFicha && !detalhe[req.id]?.ficha && (
                                <Alert severity="info" sx={{ mb: 1 }}
                                  action={
                                    temLayoutDeFicha(req.codigo_requisicao) ? (
                                      <Button size="small" onClick={(e) => {
                                        e.stopPropagation();
                                        carregarFicha(req);
                                      }}>Tentar de novo</Button>
                                    ) : null
                                  }>
                                  {detalhe[req.id].erroFicha}
                                </Alert>
                              )}

                              {detalhe[req.id]?.ficha && (() => {
                                const f = detalhe[req.id].ficha;
                                const r = detalhe[req.id].requisicao;
                                // A REQUISICOES guarda o snapshot do bipe; a ficha
                                // e o estado de agora. Divergir quer dizer que
                                // alguem mexeu no ERP depois que o bipe aconteceu.
                                //
                                // Comparar com !== dava alarme falso em 2,5% dos
                                // bipes: quantidade e INT no SQL Server e o volume
                                // da ficha e NUMERIC(9,2), entao toda formula de
                                // volume fracionario "divergia" por arredondamento.
                                // A tolerancia de 1 deixa so a diferenca real.
                                const semQuantidade = r && (r.quantidade === null || r.quantidade === undefined);
                                const divergente = r && !semQuantidade
                                  && f.quantidade_calculada != null
                                  && Math.abs(Number(r.quantidade) - Number(f.quantidade_calculada)) >= 1;
                                const itens = f.itens || [];
                                const base = itens.filter(i => i.tipo_componente === 'R');
                                const visiveis = detalhe[req.id].mostrarBase
                                  ? itens : itens.filter(i => i.tipo_componente !== 'R');
                                return (
                                  <>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {f.paciente || '(sem paciente)'}
                                    </Typography>
                                    {f.posologia && (
                                      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>
                                        “{f.posologia}”
                                      </Typography>
                                    )}
                                    <Grid container spacing={2}>
                                      <Campo rotulo="Médico" valor={f.medico} />
                                      <Campo rotulo="Cliente" valor={f.cliente?.nome} />
                                      <Campo rotulo="Forma"
                                        valor={par(f.tpformafarma, f.forma_nome)} />
                                      <Campo rotulo="Volume"
                                        valor={`${num(f.volume) ?? '-'} ${f.unidade_volume || ''}`.trim()} />
                                      <Campo rotulo="Quantidade na ficha" destaque={!divergente}
                                        valor={
                                          <Box component="span"
                                            sx={{ color: divergente ? 'error.main' : 'inherit',
                                                  fontWeight: divergente ? 'bold' : 'inherit' }}>
                                            {num(f.quantidade_calculada)}
                                            {f.unidade_volume === 'CAP' && f.qtcont > 1
                                              && ` (${num(f.volume)} × ${f.qtfor} × ${f.qtcont})`}
                                          </Box>
                                        } />
                                      <Campo rotulo="Cadastro" valor={formatarData(f.data_cadastro)} />
                                      <Campo rotulo="Retirada"
                                        valor={[formatarData(f.data_retirada), f.hora_retirada]
                                          .filter(Boolean).join(' ') || null} />
                                      <Campo rotulo="Validade" valor={formatarData(f.data_validade)} />
                                      <Campo rotulo="Origem → destino"
                                        valor={par(f.filial_estoque?.nome, f.filial_destino?.nome)
                                          && `${f.filial_estoque?.nome || '?'} → ${f.filial_destino?.nome || '?'}`} />
                                      <Campo rotulo="Ficha impressa"
                                        valor={f.flag_ficha === 'S' ? `Sim (${f.qt_impressao_ficha || 0}x)` : 'Não'} />
                                      <Campo rotulo="Conferido"
                                        valor={CONFERIDO[f.conferido] || f.conferido} />
                                      <Campo rotulo="Orçamento" valor={f.nr_orcamento} />
                                    </Grid>

                                    {detalhe[req.id].ficha?.casamento === 'aproximado' && (
                                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                                        Ficha localizada por filial + número da requisição: a série do
                                        código ({String(req.codigo_requisicao).slice(9, 11)}) não confere.
                                        Confira o paciente antes de usar este dado.
                                      </Alert>
                                    )}

                                    {semQuantidade && (
                                      <Alert severity="info" sx={{ mt: 1.5 }}>
                                        Este bipe não registrou quantidade, então não há o que comparar
                                        com a ficha.
                                      </Alert>
                                    )}

                                    {divergente && (
                                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                                        A quantidade deste bipe ({num(r.quantidade)}) não bate com a
                                        da ficha hoje ({num(f.quantidade_calculada)}) — a ficha pode
                                        ter sido alterada no ERP depois da bipagem.
                                      </Alert>
                                    )}

                                    {f.observacoes && (
                                      <Alert severity="info" sx={{ mt: 1.5 }}>{f.observacoes}</Alert>
                                    )}

                                    {itens.length > 0 && (
                                      <>
                                        <Box display="flex" alignItems="center"
                                          justifyContent="space-between" flexWrap="wrap" sx={{ mt: 2 }}>
                                          <TituloBloco>Composição — {visiveis.length} itens</TituloBloco>
                                          {base.length > 0 && (
                                            <FormControlLabel
                                              control={
                                                <Switch size="small"
                                                  checked={!!detalhe[req.id].mostrarBase}
                                                  onChange={(e) => { e.stopPropagation(); alternarBase(req.id); }} />
                                              }
                                              label={
                                                <Typography variant="caption">
                                                  mostrar a explosão da base ({base.length})
                                                </Typography>
                                              } />
                                          )}
                                        </Box>
                                        <TableContainer component={Paper} elevation={0}
                                          sx={{ maxHeight: 320 }}>
                                          <Table size="small" stickyHeader>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell>Tipo</TableCell>
                                                <TableCell>Produto</TableCell>
                                                <TableCell align="right">Quantidade</TableCell>
                                                <TableCell align="right">Pesado</TableCell>
                                                <TableCell>Lote</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {visiveis.map(i => (
                                                <TableRow key={i.itemid}>
                                                  <TableCell>
                                                    <Tooltip title={TIPO_COMPONENTE[i.tipo_componente] || 'Não catalogado'}>
                                                      <Chip size="small" variant="outlined"
                                                        label={i.tipo_componente || '?'} />
                                                    </Tooltip>
                                                  </TableCell>
                                                  <TableCell>{i.descricao}</TableCell>
                                                  <TableCell align="right">
                                                    {num(i.quantidade)} {i.unidade}
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    {i.flag_pesagem === 'S' && (
                                                      <CheckIcon sx={{ fontSize: 14, color: 'success.main', mr: 0.5 }} />
                                                    )}
                                                    {num(i.qt_pesada)}
                                                  </TableCell>
                                                  <TableCell>{i.lote || '-'}</TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </>
                                    )}
                                  </>
                                );
                              })()}

                              {/* ---------- 4. Por onde a requisição passou ---------- */}
                              {detalhe[req.id]?.trilha?.length > 0 && (
                                <>
                                  <Divider sx={{ my: 2 }} />
                                  <TituloBloco>
                                    Trilha — {detalhe[req.id].totais?.bipes} bipe(s)
                                  </TituloBloco>
                                  <TableContainer component={Paper} elevation={0}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Etapa</TableCell>
                                          <TableCell>Setor</TableCell>
                                          <TableCell>Funcionário</TableCell>
                                          <TableCell>Data / hora</TableCell>
                                          <TableCell align="right">Quantidade</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {detalhe[req.id].trilha.map(t => (
                                          <TableRow key={t.id}
                                            sx={t.atual ? { bgcolor: '#e0e7ff' } : undefined}>
                                            <TableCell>
                                              <Chip size="small" label={t.etapa || '-'}
                                                color={t.atual ? 'primary' : 'default'}
                                                variant={t.atual ? 'filled' : 'outlined'} />
                                            </TableCell>
                                            <TableCell>{t.setor}</TableCell>
                                            <TableCell>
                                              {t.funcionario_nome || t.codigo_funcionario}
                                            </TableCell>
                                            <TableCell>{t.data_formatada}</TableCell>
                                            <TableCell align="right">
                                              {num(t.quantidade)} {t.tipo_quantidade || ''}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </>
                              )}

                              {/* ---------- 5. Só aparece quando houve evento ---------- */}
                              {detalhe[req.id]?.auditoria?.length > 0 && (
                                <>
                                  <Divider sx={{ my: 2 }} />
                                  <TituloBloco>
                                    Auditoria — {detalhe[req.id].auditoria.length} evento(s)
                                  </TituloBloco>
                                  <TableContainer component={Paper} elevation={0}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Data / hora</TableCell>
                                          <TableCell>Evento</TableCell>
                                          <TableCell>Quem</TableCell>
                                          <TableCell>Setor</TableCell>
                                          <TableCell>Motivo</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {detalhe[req.id].auditoria.map(a => (
                                          <TableRow key={a.id}>
                                            <TableCell>{a.data_formatada}</TableCell>
                                            <TableCell>
                                              <Chip size="small" label={a.tipo}
                                                color={/RECUSA|EXCLUSAO|EDICAO/.test(a.tipo) ? 'error' : 'default'}
                                                variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                              {a.nome_funcionario || a.codigo_funcionario || '-'}
                                            </TableCell>
                                            <TableCell>{a.setor || '-'}</TableCell>
                                            <TableCell>
                                              <Typography variant="caption">{a.motivo || '-'}</Typography>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </>
                              )}

                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginação */}
              <TablePagination
                component="div"
                count={paginacao.total}
                page={paginacao.pagina - 1}
                onPageChange={handleChangePage}
                rowsPerPage={paginacao.limite}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Linhas por pagina:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
              />
            </>
          ) : (
            <Box textAlign="center" py={6}>
              <AssignmentIcon sx={{ fontSize: 60, color: '#e2e8f0', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Nenhuma requisicao encontrada
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Ajuste os filtros ou verifique o periodo selecionado
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Edição */}
      <Dialog
        open={dialogAberto}
        onClose={() => setDialogAberto(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6">
              Editar Requisicao
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Codigo da Requisicao"
                value={dadosFormulario.codigo_requisicao}
                onChange={(e) => setDadosFormulario(prev => ({
                  ...prev,
                  codigo_requisicao: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Data/Hora"
                type="datetime-local"
                value={dadosFormulario.data}
                onChange={(e) => setDadosFormulario(prev => ({
                  ...prev,
                  data: e.target.value
                }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Funcionario</InputLabel>
                <Select
                  value={dadosFormulario.codigo_funcionario}
                  onChange={(e) => setDadosFormulario(prev => ({
                    ...prev,
                    codigo_funcionario: e.target.value
                  }))}
                  label="Funcionario"
                >
                  <MenuItem value="">Nenhum</MenuItem>
                  {funcionarios.map((func) => (
                    <MenuItem key={func.id} value={func.codigo}>
                      {func.nome} ({func.codigo})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Setor</InputLabel>
                <Select
                  value={dadosFormulario.setor}
                  onChange={(e) => setDadosFormulario(prev => ({
                    ...prev,
                    setor: e.target.value
                  }))}
                  label="Setor"
                >
                  <MenuItem value="">Nenhum</MenuItem>
                  {setores.map((setor) => (
                    <MenuItem key={setor.id} value={setor.nome}>
                      {setor.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Capsulas"
                type="number"
                value={dadosFormulario.quantidade}
                onChange={(e) => setDadosFormulario(prev => ({
                  ...prev,
                  quantidade: e.target.value
                }))}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tipo"
                value={dadosFormulario.tipo_quantidade}
                onChange={(e) => setDadosFormulario(prev => ({
                  ...prev,
                  tipo_quantidade: e.target.value
                }))}
              />
            </Grid>
          </Grid>

          {requisicaoEditando && (
            <Box mt={3} p={2} bgcolor="#f8fafc" borderRadius={1}>
              <Typography variant="caption" color="textSecondary">
                ID do Registro: {requisicaoEditando.id}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setDialogAberto(false)}
            disabled={salvando}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={salvarRequisicao}
            disabled={salvando}
            startIcon={salvando ? <CircularProgress size={20} /> : null}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Requisicoes;
